/**
 * Formats a wallet address to show first 6 and last 4 characters
 * @param address - The wallet address to format
 * @returns Formatted address string (e.g., "0x1234...abcd")
 */
export function formatAddress(address: string | undefined): string {
  if (!address) return "";
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

/**
 * Formats a number balance to a localized string with comma separators
 * @param balance - The balance as a number
 * @returns Formatted balance string with comma separators and up to 4 decimal places
 */
export function formatBalance(balance: number | undefined): string {
  if (balance === undefined || Number.isNaN(balance)) return "0.0000";
  return Number(balance).toLocaleString("en-US", { maximumFractionDigits: 4 });
}

/**
 * Formats a USD currency value with proper comma separators and 2 decimal places
 * @param amount - The USD amount as a number
 * @returns Formatted USD string with comma separators and exactly 2 decimal places
 */
export function formatCurrency(amount: number | undefined): string {
  if (amount === undefined || Number.isNaN(amount)) return "0.00";
  return Number(amount).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Formats a token balance safely for display using the token's decimal places.
 *
 * Display rules:
 * - Use the token's decimal places as the default precision
 * - Keep trailing zeros
 * - Whole numbers show as N.00
 * - Below 0.00001 but >0 → show "<0.00001"
 * - Zero → just "0"
 *
 * Features:
 * - Uses token's decimal precision
 * - Locale-aware thousand separators
 * - Never shows scientific notation
 * - Handles edge cases gracefully
 * @param amount - Token amount (already in token units, not wei)
 * @param tokenDecimals - Number of decimal places the token supports
 * @returns Formatted balance string
 */
export function formatTokenBalance(
  amount: string | number | undefined,
  tokenDecimals: number,
): string {
  // Handle undefined/null/empty input
  if (amount === undefined || amount === null || amount === "") {
    return "0";
  }

  // Convert to number and validate
  const numAmount = typeof amount === "string" ? Number.parseFloat(amount) : amount;

  if (Number.isNaN(numAmount)) {
    return "0";
  }

  // Handle zero
  if (numAmount === 0) {
    return "0";
  }

  // Validate token decimals
  const decimals = Number.isFinite(tokenDecimals) && tokenDecimals >= 0 ? tokenDecimals : 18;

  // Use the token's decimal places
  const decimalPlaces = decimals;

  // Format the number with fixed decimal places
  return numAmount.toLocaleString("en-US", {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
}

/**
 * Extracts numeric value from formatted currency string
 * @param formattedValue - The formatted string (e.g., "$1,234.56", "1.2345 ETH")
 * @returns Numeric value or 0 if parsing fails
 */
export function extractNumericValue(formattedValue: string | undefined): number {
  if (!formattedValue || typeof formattedValue !== "string") return 0;

  // Remove common currency symbols and text, keep only numbers, decimals, and negative signs
  const cleaned = formattedValue.replace(/[$,\s%+]/g, "").replace(/[A-Za-z]+/g, "");
  const parsed = Number.parseFloat(cleaned);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Extracts numeric value from balance string with asset symbol
 * @param balanceString - The balance string (e.g., "1.2345 ETH", "9,876.0000 BTC")
 * @returns Numeric value or 0 if parsing fails
 */
export function extractBalanceValue(balanceString: string | undefined): number {
  if (!balanceString || typeof balanceString !== "string") return 0;

  // Split by space and take the first part (the numeric value)
  const parts = balanceString.split(" ");
  if (parts.length === 0) return 0;

  // Remove commas from the numeric string before parsing
  const numericString = parts[0].replace(/,/g, "");
  const parsed = Number.parseFloat(numericString);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Extracts USD value from formatted string
 * @param usdString - The USD formatted string (e.g., "$1,234.56")
 * @returns Numeric value or 0 if parsing fails
 */
export function extractUsdValue(usdString: string | undefined): number {
  if (!usdString || typeof usdString !== "string") return 0;

  // Remove $ symbol and commas
  const cleaned = usdString.replace(/[$,]/g, "");
  const parsed = Number.parseFloat(cleaned);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Formats a balance string with asset symbol to ensure proper comma formatting
 * @param balanceString - The balance string (e.g., "1234.5678 ETH", "9876.0000 BTC")
 * @returns Formatted balance string with commas (e.g., "1,234.5678 ETH", "9,876.0000 BTC")
 */
export function formatBalanceString(balanceString: string): string {
  if (!balanceString || typeof balanceString !== "string") return "0.0000";

  const parts = balanceString.split(" ");
  const numericValue = extractBalanceValue(balanceString);
  const formattedNumeric = formatBalance(numericValue);

  return parts.length > 1 ? `${formattedNumeric} ${parts[1]}` : formattedNumeric;
}

/**
 * Normalize chain ID by removing CAIP-2 prefix if present
 * @param chainId - The chain ID to normalize (e.g., "eip155:1" or "1")
 * @returns Normalized chain ID without prefix (e.g., "1")
 */
export function normalizeChainId(chainId: string): string {
  return chainId.replace(/^eip155:/, "");
}

/**
 * Formats a swap rate: amountOut per 1 unit of amountIn.
 * Uses token-style formatting with the destination token's decimals.
 *
 * Example: amountIn=2, amountOut=3, dstDecimals=6 → rate=1.5 → "1.500000"
 */
export function formatBalanceRate(
  amountIn: string | number | undefined,
  amountOut: string | number | undefined,
  dstTokenDecimals: number,
): string {
  const src =
    typeof amountIn === "number"
      ? amountIn
      : extractNumericValue(typeof amountIn === "string" ? amountIn : "");
  const dst =
    typeof amountOut === "number"
      ? amountOut
      : extractNumericValue(typeof amountOut === "string" ? amountOut : "");

  if (!Number.isFinite(src) || src <= 0) return "0";
  const rate = dst / src;
  if (!Number.isFinite(rate) || rate <= 0) return "0";

  return formatTokenBalance(rate, dstTokenDecimals);
}
